import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import {
    Box, Typography, CircularProgress, Tooltip, IconButton, Button, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent,
    Radio, RadioGroup, FormControlLabel, FormControl, Chip,
} from '@mui/material';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const BulkApproval = ({ open, onClose }) => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Checkout Approval");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [approval, setApproval] = useState('N');
    const [selectedCount, setSelectedCount] = useState(0);
    const flagStatusMap = {
        'Request Approved': { color: '#ff9800', bgColor: '#fff3e0' },
        'Article Handover': { color: '#4caf50', bgColor: '#e8f5e9' },
        'Account Sattled': { color: '#f44336', bgColor: '#ffebee' },
        'CheckOut Requested': { color: '#2196f3', bgColor: '#e3f2fd' },
        'Cancelled': { color: '#9e9e9e', bgColor: '#f5f5f5' },
    };

    const FlagChipRenderer = (params) => {
        const status = params.value || 'Unknown';
        const config = flagStatusMap[status] || { color: '#757575', bgColor: '#eeeeee' };

        return React.createElement('div', {
            style: {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                color: config.color,
                backgroundColor: config.bgColor,
                border: `1px solid ${config.color}40`,
                whiteSpace: 'nowrap',
                minWidth: '60px',
                maxWidth: '100%',
                boxSizing: 'border-box',
                height: '16px',
                lineHeight: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }
        }, status);
    };

    const mobileColDefs = useMemo(() => [
        {
            field: 'RowNum', headerName: 'Sr.No', width: 80, minWidth: 80, checkboxSelection: true, headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
        },
        { field: 'BookNo', headerName: 'Booking No', width: 100, minWidth: 100 },
        { field: 'FirstName', headerName: 'Resident', width: 100, minWidth: 100 },
        { field: 'MobileNo', headerName: 'Mobile', width: 100, minWidth: 100 },

        {
            field: 'CheReqDt', headerName: 'Request Date', width: 100, minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'Flag', headerName: 'Status', width: 100, minWidth: 130,
            cellRenderer: FlagChipRenderer,
            cellStyle: { textAlign: 'center' }
        },
    ], []);

    const desktopColDefs = useMemo(() => [
        {
            field: 'RowNum', headerName: 'Sr.No', width: 80, minWidth: 60, checkboxSelection: true, headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
        },
        { field: 'BookNo', headerName: 'Booking No', width: 100, minWidth: 100 },
        {
            field: 'BookDt', headerName: 'Book Date', width: 100, filter: 'agDateColumnFilter', minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        { field: 'FirstName', headerName: 'Resident', width: 150, minWidth: 100 },
        { field: 'MobileNo', headerName: 'Mobile', width: 100, minWidth: 100 },
        { field: 'RoomBedName', headerName: 'Bed No', width: 100, minWidth: 100 },
        { field: 'ChkReqByName', headerName: 'Requested By', width: 100, minWidth: 120 },

        { field: 'PropName', headerName: 'Property', width: 100, minWidth: 160 },
        {
            field: 'CheReqDt', headerName: 'Request Date', width: 100, filter: 'agDateColumnFilter', minWidth: 100,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'ExpCheOutDt', headerName: 'Checkout Date.', width: 100, filter: 'agDateColumnFilter', minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'Remark', headerName: 'Remark', width: 100, minWidth: 130,
            tooltipValueGetter: (params) => params.value ?? '',
        },
        {
            field: 'Flag', headerName: 'Status', width: 100, filter: 'agTextColumnFilter', minWidth: 150,
            cellRenderer: FlagChipRenderer,
            cellStyle: { textAlign: 'center' }
        },
    ], []);

    const colDefs = isMobile ? mobileColDefs : desktopColDefs;

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: !isMobile,
        resizable: true,
        floatingFilter: !isMobile,
        width: 100,
        minWidth: 80,
        tooltipValueGetter: (params) => params.value ?? '',
        wrapHeaderText: true,
        autoHeaderHeight: true,
    }), [isMobile]);

    useEffect(() => {
        fetchCheckout()
    }, [globalProperty]);

    const fetchCheckout = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`TrnBooking/GettrnChkReqDashBoard`,
                {
                    start: 1,
                    PageSize: 1,
                    SearchText: ""
                }
            );

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(bconfirm => ({
                    ...bconfirm,
                    BookDt: bconfirm.BookDt ? new Date(bconfirm.BookDt) : null,
                    BookDtFrom: bconfirm.BookDtFrom ? new Date(bconfirm.BookDtFrom) : null,
                    BookUpToDt: bconfirm.BookUpToDt ? new Date(bconfirm.BookUpToDt) : null,
                    CheReqDt: bconfirm.CheReqDt ? new Date(bconfirm.CheReqDt) : null,
                    ExpCheOutDt: bconfirm.ExpCheOutDt ? new Date(bconfirm.ExpCheOutDt) : null,
                    BookSt: bconfirm.BookSt === "C" ? "Confirmed" : bconfirm.BookSt === "B" ? "Booked" : ""
                }));
                setRowData(formattedData);
            }
        } catch (error) {
            toast.error('Error fetching check-out', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onGridReady = useCallback((params) => {
        if (isMobile) {
            setTimeout(() => {
                params.api.sizeColumnsToFit();
            }, 100);
        }
    }, [isMobile]);

    const onGridSizeChanged = useCallback((params) => {
        if (isMobile) {
            params.api.sizeColumnsToFit();
        }
    }, [isMobile]);

    // const filteredRowData = useMemo(() => {
    //     if (approval === 'Y') {
    //         return rowData.filter(row => row.AprvSt === 'Y');
    //     }

    //     return rowData.filter(row => row.AprvSt !== 'Y');
    // }, [rowData, approval]);

    const filteredRowData = useMemo(() => {
        if (approval === 'Y') {
            return rowData.filter(row => row.Flag === 'Request Approved');
        }

        return rowData.filter(row => row.Flag === 'CheckOut Requested');
    }, [rowData, approval]);


    const handleBulkApprove = async () => {
        try {
            const selectedRows = gridRef.current?.api?.getSelectedRows();

            if (!selectedRows || selectedRows.length === 0) {
                toast.warning('Please select at least one request.');
                return;
            }

            const newApprovalStatus = approval === 'N' ? 'Y' : 'N';

            const payload = selectedRows.map(row => ({
                ChkReqId: row.ChkReqId,
                BookId: row.BookId,
                AprvSt: newApprovalStatus,
                AprvBy: Number(UserId),
                AprvRemk: newApprovalStatus === 'Y' ? 'Request for check-out Approved' : 'Request for check-out UnApproved',
                UpdatedBy: Number(UserId)
            }));

            const response = await axiosInstance.post("TrnBooking/UpdateApporvedTrnChkReq", payload);

            if (response.data.Status === 0) {
                toast.success(response.data.Message || "Updated Successfully.");

                gridRef.current?.api?.deselectAll();

                await fetchCheckout();
            } else {
                toast.error(response.data.Message || "Update failed.");
            }
        } catch (error) {
            toast.error("Error while updating checkout requests.");
        }
    };

    return (
        <Box>
            <ToastContainer />
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        py: 1,
                        px: 2,
                        gap: { xs: 0.5, sm: 0 },
                    }}
                >
                    <Typography>Bulk Approval - <Chip size='small' label={selectedCount} color='primary' borderRadius={2} /></Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            ml: { xs: 0, sm: 'auto' },
                            width: { xs: '100%', sm: 'auto' },
                            flexDirection: { xs: 'column', sm: 'row' },
                        }}
                    >
                        <FormControl sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <RadioGroup row value={approval}
                                onChange={(e) => {
                                    setApproval(e.target.value);
                                    gridRef.current?.api?.deselectAll();
                                    setSelectedCount(0);
                                }}
                                sx={{
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                    flexWrap: 'nowrap',
                                }}
                            >
                                <FormControlLabel
                                    value="N"
                                    control={<Radio size="small" />}
                                    label="UnApproved"
                                />

                                <FormControlLabel
                                    value="Y"
                                    control={<Radio size="small" />}
                                    label="Approved"
                                />
                            </RadioGroup>
                        </FormControl>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'flex-end', sm: 'initial' },
                            }}
                        >
                            <Tooltip title={approval === 'Y' ? 'UnApprove Selected Requests' : 'Approve Selected Requests'} arrow>
                                <Button variant="contained" size="small"
                                    onClick={handleBulkApprove}
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: approval === 'Y' ? '#f59e0b' : '#16a34a',
                                        color: '#fff',
                                        '&:hover': {
                                            bgcolor: approval === 'Y' ? '#d97706' : '#15803d',
                                        },
                                    }}
                                >
                                    {approval === 'Y' ? 'UnApprove' : 'Approve'}
                                </Button>
                            </Tooltip>

                            <Tooltip title="Cancel Or Close" arrow>
                                <Button variant="outlined" color="error" size="small"
                                    onClick={onClose}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ padding: '8px 12px 12px !important', overflow: 'hidden', }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: 'calc(80vh - 180px)',
                            minHeight: 400,
                            backgroundColor: 'rgb(249, 249, 249)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            className={`table-grid ag-theme-alpine ${isMobile ? 'ag-grid-mobile' : ''}`}
                            sx={{
                                height: '100%',
                                width: '100%',
                                position: 'relative',
                                '&.ag-grid-mobile .ag-cell': {
                                    fontSize: '12px',
                                    padding: '0 4px',
                                },
                                '&.ag-grid-mobile .ag-header-cell-text': {
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                }
                            }}
                        >
                            {isLoading ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 10,
                                        textAlign: 'center',
                                    }}
                                >
                                    <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
                                    <Typography sx={{
                                        marginTop: 2,
                                        color: '#635bff',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}>
                                        Loading data...
                                    </Typography>
                                </Box>
                            ) : (
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={filteredRowData}
                                    columnDefs={colDefs}
                                    defaultColDef={defaultColDef}
                                    pagination={true}
                                    paginationPageSize={isMobile ? 20 : 1000}
                                    paginationPageSizeSelector={isMobile ? [20, 50, 100] : [100, 500, 1000, 5000]}
                                    onGridReady={onGridReady}
                                    onGridSizeChanged={onGridSizeChanged}
                                    enableCellTextSelection={true}
                                    rowHeight={isMobile ? 30 : 25}
                                    headerHeight={isMobile ? 35 : 30}
                                    rowSelection="multiple"
                                    suppressRowClickSelection={true}
                                    onSelectionChanged={(params) => {
                                        setSelectedCount(params.api.getSelectedRows().length);
                                    }}
                                    floatingFiltersHeight={28}
                                    suppressHorizontalScroll={false}
                                    domLayout={isMobile ? "autoHeight" : "normal"}
                                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                                    tooltipShowDelay={0}
                                    suppressColumnVirtualisation={isMobile}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: { xs: 8, sm: 8 },
                                left: { xs: 20, sm: 40 },
                                fontSize: { xs: '12px', sm: '13px' },
                                color: 'black',
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                padding: { xs: '2px 8px', sm: '3px 10px' },
                                borderRadius: '4px',
                            }}
                        >
                            Total Records: <b>{filteredRowData.length}</b>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default BulkApproval;
